/**
 * One proposal card inside the Build-with-AI panel (S9): title + suggested
 * type, collapsible SQL review, an interactive read-only Preview (own
 * `useTilePreview` instance -> `TrustStrip`, same primitive the Add Tile
 * dialog uses), Reject, and Add. The panel owns the actual add/build logic
 * (it needs its own preview run to shape the tile) — this card is presentation
 * + the interactive review preview only.
 */
import { Button } from '@/components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { TileProposal } from '../services/aiTileProposal';
import { TrustStrip } from './add-tile/TrustStrip';
import { useTilePreview } from './add-tile/useTilePreview';

export type AiProposalStatus = 'pending' | 'adding' | 'added' | 'rejected' | 'failed';

export function AiProposalCard({
  proposal,
  status,
  errorMessage,
  connectionId,
  database,
  dashboardId,
  onReject,
  onAdd,
}: {
  proposal: TileProposal;
  status: AiProposalStatus;
  errorMessage?: string;
  connectionId: string;
  database?: string;
  dashboardId: string;
  onReject: () => void;
  onAdd: () => void;
}) {
  const preview = useTilePreview();
  const settled = status === 'added' || status === 'rejected';

  return (
    <div
      className="rounded-md border border-border p-2.5"
      data-testid="ai-proposal-card"
      data-status={status}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium">
        <span className="truncate">{proposal.title}</span>
        <span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {proposal.tileType}
        </span>
        <span className="flex-1" />
        {status === 'added' && (
          <span
            className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400"
            data-testid="ai-proposal-added-badge"
          >
            Added ✓
          </span>
        )}
        {status === 'rejected' && (
          <span
            className="text-[10px] font-medium text-muted-foreground"
            data-testid="ai-proposal-rejected-badge"
          >
            Rejected
          </span>
        )}
      </div>

      <details className="mt-1.5">
        <summary
          className="cursor-pointer text-[11px] text-muted-foreground"
          data-testid="ai-proposal-review-toggle"
        >
          Review SQL
        </summary>
        <pre
          className="mt-1.5 overflow-x-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[10.5px] text-muted-foreground"
          data-testid="ai-proposal-sql"
        >
          {proposal.sql}
        </pre>
      </details>

      {preview.state.status !== 'idle' && (
        <div className="mt-1.5">
          <TrustStrip
            preview={preview.state}
            durationsMs={preview.durationsMs}
            refresh={{ mode: 'manual', seconds: 30 }}
          />
        </div>
      )}

      {status === 'failed' && errorMessage && (
        <p className="mt-1.5 text-[11px] text-destructive" data-testid="ai-proposal-error">
          {errorMessage}
        </p>
      )}

      {status === 'adding' && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <RandomDotmSpinner size={12} />
          Adding…
        </div>
      )}

      {!settled && status !== 'adding' && (
        <div className="mt-2 flex items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-6 text-xs"
            onClick={() => preview.run({ connectionId, database, sql: proposal.sql, dashboardId })}
            disabled={preview.state.status === 'running'}
            data-testid="ai-proposal-preview-button"
          >
            {preview.state.status === 'running' ? <RandomDotmSpinner size={12} /> : 'Preview'}
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-6 text-xs"
            onClick={onAdd}
            data-testid="ai-proposal-add-button"
          >
            {status === 'failed' ? 'Retry' : '+ Add this tile'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 text-xs text-muted-foreground"
            onClick={onReject}
            data-testid="ai-proposal-reject-button"
          >
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}
